/** Вариант выбора автокомплита. */
export interface ComboboxOption {
  value: string;
  label: string;
}

/** Страница вариантов с сервера. */
export interface OptionsPage {
  items: readonly ComboboxOption[];
  hasMore: boolean;
}

/**
 * Загрузка страницы вариантов: строка поиска, сдвиг и сигнал отмены. null — загрузить не
 * удалось (покажем «Повторить»). Подключённый компонент привязывает её к маршруту API.
 */
export type LoadOptions = (query: string, offset: number, signal: AbortSignal) => Promise<OptionsPage | null>;

export interface AsyncOptionsState {
  items: readonly ComboboxOption[];
  /** Строка поиска, которой соответствуют items. */
  query: string;
  /** Ждём паузы в вводе или ответа сервера. */
  loading: boolean;
  failed: boolean;
  hasMore: boolean;
  /** Хотя бы одна страница уже пришла. */
  loaded: boolean;
}

/** Таймеры — параметром, чтобы тесты управляли временем сами. */
export interface Scheduler {
  setTimeout(run: () => void, ms: number): unknown;
  clearTimeout(handle: unknown): void;
}

/** Пауза в вводе, после которой уходит поиск. */

const browserScheduler: Scheduler = {
  setTimeout: (run, ms) => globalThis.setTimeout(run, ms),
  clearTimeout: (handle) => globalThis.clearTimeout(handle as ReturnType<typeof globalThis.setTimeout>),
};

const INITIAL: AsyncOptionsState = { items: [], query: '', loading: false, failed: false, hasMore: false, loaded: false };

/**
 * Варианты автокомплита с сервера (ENT-UC-8.CC-2, UX-стереотип «Combobox»): первая страница при
 * открытии, поиск после паузы в вводе, следующая страница по запросу. Прошлый запрос
 * отменяется, а его ответ отбрасывается — медленный ответ на «Бл» не заменит ответ на «Black».
 * Пока идёт новый поиск, на экране остаются прежние варианты.
 */
export class AsyncOptions {
  private state: AsyncOptionsState = INITIAL;
  private readonly listeners = new Set<() => void>();
  private controller: AbortController | null = null;
  private timer: unknown = null;
  private request = 0;
  private last: { query: string; offset: number } | null = null;

  constructor(
    private load: LoadOptions,
    private readonly scheduler: Scheduler = browserScheduler,
    private readonly delayMs: number,
  ) {}

  /** Свежая функция загрузки: компонент перерисовался с новыми props. */
  setLoader(load: LoadOptions): void {
    this.load = load;
  }

  readonly subscribe = (listener: () => void): (() => void) => {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  };

  readonly getState = (): AsyncOptionsState => this.state;

  /** Список открыли: первая страница сразу, если для этой строки её ещё нет. */
  open(query: string): void {
    this.cancelTimer();
    if (this.state.loaded && !this.state.failed && this.state.query === query) return;
    void this.fetch(query, 0);
  }

  /** Изменился ввод: поиск — после паузы. */
  search(query: string): void {
    this.cancelTimer();
    this.set({ loading: true, failed: false });
    this.timer = this.scheduler.setTimeout(() => {
      this.timer = null;
      void this.fetch(query, 0);
    }, this.delayMs);
  }

  /** Следующая страница для показанной строки поиска. */
  loadMore(): void {
    const { loading, failed, hasMore, query, items } = this.state;
    if (loading || failed || !hasMore) return;
    void this.fetch(query, items.length);
  }

  /** Повторить запрос, который не удался. */
  retry(): void {
    if (this.state.failed && this.last) void this.fetch(this.last.query, this.last.offset);
  }

  /** Список закрыли: ожидание и запрос в полёте отменяются, загруженное остаётся. */
  close(): void {
    this.cancelTimer();
    this.abort();
    this.request += 1;
    if (this.state.loading) this.set({ loading: false });
  }

  private async fetch(query: string, offset: number): Promise<void> {
    this.abort();
    const controller = new AbortController();
    this.controller = controller;
    const id = ++this.request;
    this.last = { query, offset };
    this.set({ loading: true, failed: false });
    let page: OptionsPage | null;
    try {
      page = await this.load(query, offset, controller.signal);
    } catch {
      page = null;
    }
    if (id !== this.request) return;
    this.controller = null;
    if (!page) {
      this.set({ loading: false, failed: true });
      return;
    }
    this.set({
      items: offset === 0 ? page.items : [...this.state.items, ...page.items],
      query,
      hasMore: page.hasMore,
      loading: false,
      failed: false,
      loaded: true,
    });
  }

  private abort(): void {
    this.controller?.abort();
    this.controller = null;
  }

  private cancelTimer(): void {
    if (this.timer === null) return;
    this.scheduler.clearTimeout(this.timer);
    this.timer = null;
  }

  private set(patch: Partial<AsyncOptionsState>): void {
    this.state = { ...this.state, ...patch };
    for (const listener of this.listeners) listener();
  }
}
